// @flow

import type { State } from "@store/reducers";
import type { StandardQuestion } from "@modules/service/reducer/standard-questions/types";
import { ApprovalStateEnum } from "@modules/service/constants";
import type { HierarchyAssignment, ApprovableContainer } from "@modules/service/types";
import { removeNullAndUndefinedProps } from "@transformers/object";
import Ajv from "ajv";
import { validateHierarchyAssignmentSchema } from "./index";

const { DRAFT_ONLY, ACTIVE_WITH_DRAFT } = ApprovalStateEnum;

export type GetStandardQuestionsRevisionList = (state: State) => Array<StandardQuestion>;

export const getStandardQuestionsList = (
  state: State,
): Array<ApprovableContainer<StandardQuestion>> =>
  (state.service.standardQuestions.list || {}).content || [];

export const getStandardQuestionsActiveList: GetStandardQuestionsRevisionList = (state: State) => {
  return getStandardQuestionsList(state)
    .filter(c => c.approvalState.indexOf("ACTIVE_") !== -1)
    .reduce((acc, c) => (c.active ? [...acc, c.active] : acc), []);
};

export const getStandardQuestionsPendingList: GetStandardQuestionsRevisionList = (state: State) => {
  return getStandardQuestionsList(state)
    .filter(c => c.approvalState.indexOf("PENDING_REVIEW") !== -1)
    .reduce((acc, c) => (c.draft ? [...acc, c.draft] : acc), []);
};

export const getStandardQuestionsDraftList: GetStandardQuestionsRevisionList = (state: State) => {
  return getStandardQuestionsList(state)
    .filter(c => c.approvalState === DRAFT_ONLY || c.approvalState === ACTIVE_WITH_DRAFT)
    .reduce((acc, c) => (c.draft ? [...acc, c.draft] : acc), []);
};

export const getStandardQuestionContainer = (
  state: State,
): ?ApprovableContainer<StandardQuestion> => state.service.standardQuestions.current;

export const getStandardQuestionDraft = (state: State): ?StandardQuestion =>
  state.service.standardQuestions.draft;

export const isStandardQuestionDraftValid = (state: State): boolean => {
  const schema = getStandardQuestionRevisionSchema(state);
  const draft = (((state.service || {}).standardQuestions || {}).current || {}).draft;
  if (!schema || !draft) {
    return false;
  }
  return new Ajv().validate(schema, removeNullAndUndefinedProps(draft));
};

// MODALS
export const getShowAddStandardQuestionModal = (state: State): boolean =>
  state.service.standardQuestions.modals.showAddStandardQuestionModal;

export const getShowEditStandardQuestionModal = (state: State): boolean =>
  state.service.standardQuestions.modals.showEditModal;

export const getStandardQuestionsModalStep = (state: State): number =>
  state.service.standardQuestions.modals.modalStep;

export const getStandardQuestionRevisionSchema = (state: State): ?Object =>
  state.service.standardQuestions.schema;

export const getStandardQuestionAssignments = (state: State): Array<HierarchyAssignment> =>
  state.service.standardQuestions.assignments;

export const getStandardQuestionDraftAssignments = (state: State): Array<HierarchyAssignment> =>
  state.service.standardQuestions.draftAssignments;

export const isStandardQuestionDraftAssignmentsValid = (state: State): boolean => {
  const assignments = getStandardQuestionDraftAssignments(state);
  return validateHierarchyAssignmentSchema(assignments);
};

export const isStandardQuestionAssignmentsValid = (state: State): boolean => {
  const assignments = getStandardQuestionAssignments(state);
  return validateHierarchyAssignmentSchema(assignments);
};

export const getShowStandardQuestionSubmitModal = (state: State): boolean =>
  state.service.standardQuestions.modals.showSubmitModal;

export const getShowStandardQuestionCancelModal = (state: State): boolean =>
  state.service.standardQuestions.modals.showCancelModal;

export const getShowStandardQuestionDiscardModal = (state: State): boolean =>
  state.service.standardQuestions.modals.showDiscardModal;

export const getShowStandardQuestionCancelDiscardModal = (state: State): boolean =>
  state.service.standardQuestions.modals.showCancelDiscardModal;

export const getShowStandardQuestionDisableModal = (state: State): boolean =>
  state.service.standardQuestions.modals.showDisableModal;

export const getShowStandardQuestionEditAssignmentModal = (state: State): boolean =>
  state.service.standardQuestions.modals.showEditAssignmentsModal;
